import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { fetchHealth } from './api/health.ts'
import { useAuth } from './auth-context.ts'
import {
  EdgeError,
  edgeSyncStatus,
  EdgeUnreachableError,
  type EdgeSyncStatus,
} from './edge-api.ts'

/**
 * Contra quem o balcão está vendendo (F042).
 *
 * ## Por que a escolha é da tela, e não do proxy
 *
 * Um fallback transparente em Rust — "se a nuvem não responder, mande para o
 * Edge" — parece mais elegante e é errado por três razões, na ordem em que
 * pesam:
 *
 * **Os contratos são diferentes**, e isso está escrito no código: o cabeçalho
 * de `apps/edge/src/routes/till.ts` diz que ele deliberadamente não reproduz as
 * formas de `/api/v1`. Traduzir payloads no proxy seria uma terceira
 * implementação do contrato, numa linguagem que não tem nenhum dos pacotes de
 * domínio.
 *
 * **O operador precisa ver.** A venda no Edge sai com referência local, sem
 * número de documento e sem cliente cadastrado. Um caixa que não sabe em que
 * modo está promete ao cliente uma nota que não vai sair naquele instante.
 *
 * Isto corrige o que a F017 dizia — *"a troca é invisível para o operador"*.
 * Certo sobre **não exigir ação**; errado sobre **não ser mostrada**.
 *
 * ## Capacidades, e não `if (mode === 'edge')`
 *
 * O que muda entre os dois modos é descrito por `capabilities`. Espalhar
 * comparações de modo pela tela produziria, em três features, quinze
 * condicionais e duas versões que divergem — que é o que a §26 do Master Spec
 * proíbe. Uma tela, uma árvore de render, e as diferenças declaradas.
 */

export type TillMode =
  /** A nuvem responde. É o normal, e tudo funciona. */
  | 'cloud'
  /** A nuvem não responde e há um Edge vivo nesta máquina. */
  | 'edge'
  /**
   * A nuvem não responde e não há Edge aqui.
   *
   * O único estado em que o PDV recusa — e recusa dizendo o que falta, em vez
   * de deixar alguém escanear um item e descobrir no pagamento.
   */
  | 'none'

export type TillCapabilities = {
  /** Identificar o cliente. O Edge vende para o Consumidor Final e mais nada. */
  readonly customer: boolean
  /** Salvar orçamento. Não existe no Edge. */
  readonly quote: boolean
  /**
   * Despachar uma entrega com o motoboy (F101).
   *
   * Não existe no Edge pelas mesmas duas razões do orçamento: o pedido nasce
   * numerado, e a saída levanta um título a receber — numeração e financeiro
   * são da nuvem.
   */
  readonly delivery: boolean
  /** A venda sai numerada. Offline não: a numeração é da nuvem. */
  readonly documentNumber: boolean
  /** Consultar crédito do cliente. Depende do financeiro, que é da nuvem. */
  readonly credit: boolean
  /** Escolher a tabela de preço da linha. O Edge tem uma tabela só. */
  readonly priceList: boolean
  /** Cancelar a venda que acabou de sair. */
  readonly cancel: boolean
}

/**
 * O que a loja autorizou, e não o que este balcão sabe fazer.
 *
 * Separado de `TillCapabilities` de propósito: aquilo descreve a DIFERENÇA
 * entre os dois modos — o que o Edge não faz —, e isto é a mesma resposta nos
 * dois. Misturar as duas coisas faria parecer que vender no negativo é uma
 * limitação do offline, quando é uma decisão da loja.
 */
export type TillPolicy = {
  /** `company.allow_negative_stock` (F103). */
  readonly allowNegativeStock: boolean
}

const CLOUD: TillCapabilities = {
  customer: true,
  quote: true,
  delivery: true,
  documentNumber: true,
  credit: true,
  priceList: true,
  cancel: true,
}

/**
 * O que o Edge NÃO faz, e por quê — cada `false` aqui tem uma razão.
 *
 * `customer`: o snapshot leva um cliente só, o Consumidor Final. Levar a
 * carteira inteira para cada balcão seria descer dados pessoais de toda a base
 * para um PC de loja, para atender o caso raro de alguém querer nota em nome
 * durante uma queda de internet.
 *
 * `documentNumber`: numerar é da nuvem. Um Edge que inventasse a própria série
 * produziria dois documentos com o mesmo número no dia em que dois balcões
 * vendessem offline ao mesmo tempo — e ninguém descobriria até a apuração.
 *
 * `cancel`: cancelar uma venda que ainda não subiu é apagar um fato que a
 * nuvem nunca soube que existiu. Tecnicamente fácil, e é justamente por isso
 * que fica de fora até alguém decidir o que significa.
 */
const EDGE: TillCapabilities = {
  customer: false,
  quote: false,
  delivery: false,
  documentNumber: false,
  credit: false,
  priceList: false,
  cancel: false,
}

/**
 * Este caixa e o Edge da loja (F197).
 *
 *   `paired`   — o Edge respondeu a este caixa.
 *   `unpaired` — o Edge existe e recusou este caixa (401 `device_*`): falta
 *                parear, e isso se resolve numa tela, **com internet**.
 *   `absent`   — não há Edge aqui: nenhum configurado, ou ele não respondeu.
 */
export type EdgePairing = 'paired' | 'unpaired' | 'absent'

export type TillBackend = {
  readonly mode: TillMode
  readonly capabilities: TillCapabilities
  /**
   * A política da loja, quando este balcão a conhece.
   *
   * No Edge vem do snapshot; na nuvem a tela lê a empresa direto e ignora
   * isto. Nulo enquanto o Edge não respondeu.
   */
  readonly policy: TillPolicy | null
  /**
   * Vendas feitas aqui que a nuvem ainda não recebeu.
   *
   * Um número que sobe e nunca desce é o sinal que faz alguém olhar a internet
   * da loja — e é por isso que ele fica na tela o tempo todo no modo Edge, em
   * vez de num menu.
   */
  readonly pending: number
  /** Vendas que a nuvem recusou ao subir (F197). */
  readonly refused: number
  /** Se o Edge ainda aceita venda nova, e há quantas horas está sem subir. */
  readonly offline: NonNullable<EdgeSyncStatus['offline']> | null
  readonly edgePairing: EdgePairing
  /** Se a pergunta "existe Edge aqui?" ainda não foi respondida. */
  readonly isDeciding: boolean
}

type EdgeAnswer =
  | { readonly state: 'paired'; readonly status: EdgeSyncStatus }
  | { readonly state: 'unpaired' }
  | { readonly state: 'absent' }

/**
 * O que o Edge respondeu, lido sem otimismo (F197).
 *
 * Antes, **qualquer** resposta HTTP contava como "há Edge" — inclusive o 401
 * de um caixa nunca pareado e o 503 do proxy dizendo que não há Edge nenhum.
 * O PDV entrava no modo Edge e falhava em cada bipe. Agora só a resposta de
 * verdade vende; o 401 é "falta parear"; o resto é ausência.
 */
export const readEdgeAnswer = async (
  signal?: AbortSignal
): Promise<EdgeAnswer> => {
  try {
    return { state: 'paired', status: await edgeSyncStatus(signal) }
  } catch (error) {
    if (error instanceof EdgeError && error.status === 401) {
      return { state: 'unpaired' }
    }
    if (error instanceof EdgeError || error instanceof EdgeUnreachableError) {
      return { state: 'absent' }
    }
    throw error
  }
}

/** A mesma pergunta que o selo "Online" faz (`components/api-status.tsx`). */
export const HEALTH_QUERY_KEY = ['health'] as const

/** A nuvem só "caiu" depois de falhar por este tempo seguido (F212). */
export const CLOUD_DOWN_AFTER_MS = 15_000
const HEALTH_RETRY_MS = 5_000

/**
 * Contra quem vender agora.
 *
 * **A nuvem caiu** é decidido por duas fontes, e basta uma (F197):
 *
 *   - a saúde da nuvem (`/health`, a cada 30 s, a mesma consulta do selo) —
 *     é o que pega a queda **no meio do dia**, quando a sessão da manhã ainda
 *     está em memória e `isUnverified` continua falso;
 *   - `isUnverified` — o app aberto já sem internet, com a sessão lembrada.
 *
 * Antes só a segunda contava, e só abrindo o programa sem internet o balcão
 * passava ao Edge. E no aplicativo instalado nem isso: o proxy respondia 503
 * e a web não o lia como queda (ver `api/client.ts`).
 */
export const decideTillMode = (input: {
  readonly cloudDown: boolean
  readonly edge: EdgeAnswer['state'] | undefined
}): TillMode =>
  !input.cloudDown ? 'cloud' : input.edge === 'paired' ? 'edge' : 'none'

export const useTillBackend = (): TillBackend => {
  const { isUnverified } = useAuth()

  const health = useQuery({
    queryKey: HEALTH_QUERY_KEY,
    queryFn: ({ signal }) => fetchHealth(signal),
    // Falhou: pergunta de novo a cada 5 s, para voltar logo (F212).
    refetchInterval: (query) =>
      query.state.status === 'error' ? HEALTH_RETRY_MS : 30_000,
    refetchIntervalInBackground: false,
    retry: 1,
    throwOnError: false,
  })

  /*
   * Desde quando a nuvem não responde (F212).
   *
   * Duas falhas com um segundo de intervalo trocavam o PDV inteiro pela tela
   * "Sem conexão com o servidor" por 30 s — com o cliente na frente. Agora só
   * uma queda que DURA conta; uma falha solta fica invisível.
   */
  // A última resposta foi falha? `isError` não basta: sem nenhuma resposta
  // boa antes, a consulta volta a "pendente" a cada nova tentativa.
  const lastFailed = health.errorUpdatedAt > health.dataUpdatedAt
  const [failingSince, setFailingSince] = useState<number | null>(null)
  // Ajustado durante o render, como a documentação do React recomenda para
  // estado derivado de uma mudança: sem efeito e sem um render atrasado.
  if (lastFailed && failingSince === null) {
    setFailingSince(health.errorUpdatedAt)
  }
  if (!lastFailed && failingSince !== null) setFailingSince(null)
  const sustained =
    lastFailed &&
    failingSince !== null &&
    health.errorUpdatedAt - failingSince >= CLOUD_DOWN_AFTER_MS

  const edge = useQuery({
    queryKey: ['edge', 'sync-status'],
    queryFn: async ({ signal }) => await readEdgeAnswer(signal),
    /*
     * Perguntado o tempo todo, e não só quando a nuvem cai.
     *
     * Descobrir que existe um Edge no instante em que a internet acaba é
     * descobrir tarde: o balcão ficaria alguns segundos sem saber o que
     * oferecer, com um cliente na frente. E é com a internet no ar que o caixa
     * precisa saber que falta parear — sem ela não dá. A pergunta é barata:
     * é `127.0.0.1`.
     */
    refetchInterval: 30_000,
    retry: false,
    /*
     * Nunca `throwOnError`: um PC sem Edge é o caso comum, e um erro de
     * consulta borbulhando daqui derrubaria a tela do PDV inteira por causa
     * de um recurso que aquela máquina não tem.
     */
    throwOnError: false,
  })

  const cloudDown = isUnverified || sustained
  const answer = edge.data
  const mode = decideTillMode({ cloudDown, edge: answer?.state })
  const status = answer?.state === 'paired' ? answer.status : null

  return {
    mode,
    capabilities: mode === 'edge' ? EDGE : CLOUD,
    policy:
      status === null
        ? null
        : { allowNegativeStock: status.allowNegativeStock ?? false },
    pending: status?.pending ?? 0,
    refused: status?.refused ?? 0,
    offline: status?.offline ?? null,
    edgePairing: answer?.state ?? 'absent',
    /*
     * Só é "decidindo" quando a resposta muda alguma coisa: com a nuvem no ar,
     * o PDV abre agora, e a existência de um Edge é irrelevante até ela cair.
     */
    isDeciding: cloudDown && edge.isPending,
  }
}
